import { BaseCondition } from "@tiny/api";

type AttributeType = NonNullable<BaseCondition["attributeType"]>;

export type DisabledAttributeTypes = Partial<Record<AttributeType, string>>;

export const SENDER_NEEDS_ACTIVITY_HINT =
  "Add an Activity condition (e.g. Opened) to this group first";
export const LINK_URL_NEEDS_CLICKED_HINT =
  "Add an Activity → Clicked condition to this group first";
export const SENDER_ONLY_ACTIVITY_HINT =
  "Sender filters an Activity condition — add it as a new condition";
export const LINK_URL_ONLY_CLICKED_HINT =
  "Link URL filters an Activity → Clicked condition — add it as a new condition";

export const SENDER_MISSING_ACTIVITY_ERROR =
  "Sender needs an Activity condition in this group";
export const LINK_URL_MISSING_CLICKED_ERROR =
  "Link URL needs an Activity → Clicked condition in this group";

function isActivity(cond: any) {
  return cond?.attributeType === "ACTIVITY";
}

function isClicked(cond: any) {
  return isActivity(cond) && cond?.attributeName === "Clicked";
}

/**
 * SENDER and LINK_URL only narrow an Activity (resp. Activity → Clicked)
 * condition of the same group, so they are offered only while one exists —
 * and not on the row that holds the group's only such condition, because
 * switching that row would remove the condition the filter depends on.
 */
export function getDisabledAttributeTypes(
  conditions: any[] | undefined,
  rowIndex: number
): DisabledAttributeTypes {
  const all = Array.isArray(conditions) ? conditions : [];
  const others = all.filter((_, i) => i !== rowIndex);
  const result: DisabledAttributeTypes = {};

  if (!all.some(isActivity)) {
    result.SENDER = SENDER_NEEDS_ACTIVITY_HINT;
  } else if (!others.some(isActivity)) {
    result.SENDER = SENDER_ONLY_ACTIVITY_HINT;
  }

  if (!all.some(isClicked)) {
    result.LINK_URL = LINK_URL_NEEDS_CLICKED_HINT;
  } else if (!others.some(isClicked)) {
    result.LINK_URL = LINK_URL_ONLY_CLICKED_HINT;
  }

  return result;
}

/**
 * Validation message for a row whose SENDER / LINK_URL filter has lost the
 * Activity condition it depends on, or undefined when the row is valid.
 */
export function getDependentConditionError(
  attributeType: string | undefined,
  conditions: any[] | undefined
): string | undefined {
  const all = Array.isArray(conditions) ? conditions : [];
  if (attributeType === "SENDER" && !all.some(isActivity)) {
    return SENDER_MISSING_ACTIVITY_ERROR;
  }
  if (attributeType === "LINK_URL" && !all.some(isClicked)) {
    return LINK_URL_MISSING_CLICKED_ERROR;
  }
  return undefined;
}
